import { Err, err, errAsync, ok, Result, ResultAsync } from 'neverthrow'
import { AbortFail } from '../Fail/AbortFail'

export interface TAbortable<T, E> extends ResultAsync<T, E | AbortFail> {}

const from = <T, E>(
  promiseLike: ResultAsync<T, E>,
  signal: AbortSignal,
  // eslint-disable-next-line @typescript-eslint/promise-function-async
): TAbortable<T, E> => {
  if (signal.aborted) {
    return errAsync(AbortFail.from(signal))
  }

  const original = Promise.resolve(promiseLike)

  let removeListener: (() => void) | null = null

  const abortable = new Promise<Result<T, E | AbortFail>>(resolve => {
    const onAbort = (): void => {
      removeListener?.()
      const fail =
        signal.reason instanceof AbortFail
          ? signal.reason
          : AbortFail.from(signal)
      resolve(err(fail))
      void original.catch(() => {})
    }
    if (signal !== undefined) {
      if (signal.aborted) {
        onAbort()
        return
      }

      signal.addEventListener('abort', onAbort, { once: true })
      removeListener = () => signal.removeEventListener('abort', onAbort)
    }

    original.then(
      it => {
        if (signal.aborted) onAbort()
        else resolve(it)
      },
      () => {},
    )
  }).finally(() => {
    if (typeof removeListener !== 'function') return

    removeListener()
    removeListener = null
  })

  return new ResultAsync(abortable)
}

const wasAborted = <T, E>(
  it: Result<T, E | AbortFail>,
): it is Err<T, AbortFail> => {
  return it.isErr() && it.error instanceof AbortFail
}

const match = async <T, E, R>(
  abortable: TAbortable<T, E | AbortFail>,
  {
    onAbort,
    onContinue,
    onFinally,
  }: {
    onAbort: (err: AbortFail) => R
    onContinue: (result: Result<T, E>) => R
    onFinally?: (output: R) => void
  },
): Promise<R> => {
  const awaitedAbortable = await abortable
  const output: R = awaitedAbortable.match(
    value => onContinue(ok(value)),
    error => {
      if (error instanceof AbortFail) return onAbort(error)
      return onContinue(err(error))
    },
  )

  onFinally?.(output)

  return output
}

export const Abortable = {
  from,
  wasAborted,
  match,
}
